import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

/**
 * LevelUpHeader - Replicates the header design from skillupwithlevelup.com
 * Maintains existing authentication/profile UI logic
 */
function LevelUpHeader({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Navigation items - adapted for Compete Portal
  const navItems = [
    { label: t('nav.contentLibrary'), path: '/browse' },
    { label: t('nav.analytics'), path: '/analytics', roles: ['admin', 'report_viewer', 'content_owner'] },
    { label: t('nav.admin'), path: '/admin', roles: ['admin'] },
  ];

  // Check if user has required role for nav item
  const hasAccess = (item) => {
    if (!item.roles) return true;
    if (!user) return false;
    return item.roles.some(role => user.roles?.includes(role));
  };

  // Handle logout with redirect to LevelUp logout
  const handleLogout = async () => {
    try {
      // First execute existing logout logic
      if (onLogout) {
        onLogout();
      }
      
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // After logout completes, redirect to LevelUp logout
      const levelupUrl = process.env.REACT_APP_LEVELUP_URL || 'https://skillupwithlevelup.com';
      window.location.href = `${levelupUrl}/logout`;
    } catch (error) {
      console.error('Logout error:', error);
      // Still redirect even if there's an error
      const levelupUrl = process.env.REACT_APP_LEVELUP_URL || 'https://skillupwithlevelup.com';
      window.location.href = `${levelupUrl}/logout`;
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      style={{ 
        backgroundColor: '#1B5E5F', 
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}
      role="banner"
    >
      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '70px'
      }}>
        {/* Left Section - Logos: Technofocus | LevelUp | MS-LevelUp */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Technofocus Logo - white filtered */}
          <a 
            href="https://technofocus.co" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            aria-label="Technofocus website (opens in new tab)"
          >
            <img 
              src="/technofocus-logo.png" 
              alt="Technofocus" 
              style={{ 
                height: '20px', 
                width: 'auto',
                filter: 'brightness(0) invert(1)'
              }}
            />
          </a>
          
          {/* Pipe Separator */}
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px', fontWeight: '200' }} aria-hidden="true">|</span>
          
          {/* LevelUp Logo - Light SVG version */}
          <a 
            href="https://skillupwithlevelup.com" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            aria-label="LevelUp website (opens in new tab)"
          >
            <img 
              src="/levelup-logo-light.svg" 
              alt="LevelUp" 
              style={{ height: '20px', width: 'auto', marginTop: '7px' }}
            />
          </a>
          
          {/* Pipe Separator */}
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px', fontWeight: '200' }} aria-hidden="true">|</span>
          
          {/* MS-LevelUp Logo */}
          <img 
            src="/ms-levelup-logo.svg" 
            alt="Sponsored by Microsoft" 
            style={{ height: '28px', width: 'auto' }}
          />
        </div>

        {/* Center Section - Navigation (Desktop) */}
        <nav 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            '@media (max-width: 900px)': { display: 'none' }
          }} 
          className="desktop-nav"
          aria-label="Main navigation"
        >
          {navItems.filter(hasAccess).map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              aria-current={isActive(item.path) ? 'page' : undefined}
              style={{
                background: isActive(item.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '500',
                padding: '10px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.outline = '2px solid #C4E538';
                e.target.style.outlineOffset = '2px';
              }}
              onBlur={(e) => {
                e.target.style.outline = 'none';
              }}
              onMouseEnter={(e) => {
                if (!isActive(item.path)) {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(item.path)) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              {item.label}
            </button>
          ))}
          
          {/* Upload button for content owners */}
          {user && (user.roles?.includes('content_owner') || user.roles?.includes('admin')) && (
            <button
              onClick={() => navigate('/upload')}
              aria-current={isActive('/upload') ? 'page' : undefined}
              style={{
                background: isActive('/upload') ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '500',
                padding: '10px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.outline = '2px solid #C4E538';
                e.target.style.outlineOffset = '2px';
              }}
              onBlur={(e) => {
                e.target.style.outline = 'none';
              }}
              onMouseEnter={(e) => {
                if (!isActive('/upload')) {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive('/upload')) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              {t('nav.uploadContent')}
            </button>
          )}
        </nav>

        {/* Right Section - User Profile & Language */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LanguageSelector />
          {user ? (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                data-testid="user-profile-button"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  color: '#FFFFFF'
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#C4E538',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1B5E5F',
                  fontWeight: '600',
                  fontSize: '14px'
                }}>
                  {user.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span style={{ 
                  fontSize: '14px', 
                  fontWeight: '500',
                  maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }} className="desktop-only">
                  {user.name || 'User'}
                </span>
                <ChevronDown size={16} style={{ 
                  transition: 'transform 0.2s',
                  transform: profileDropdownOpen ? 'rotate(180deg)' : 'rotate(0)'
                }} />
              </button>

              {/* Profile Dropdown */}
              {profileDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  backgroundColor: '#FFFFFF',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  minWidth: '220px',
                  overflow: 'hidden',
                  zIndex: 1001
                }}>
                  {/* User Info */}
                  <div style={{ 
                    padding: '16px', 
                    borderBottom: '1px solid #E5E7EB',
                    backgroundColor: '#F9FAFB'
                  }}>
                    <p style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#1F2937',
                      margin: 0,
                      marginBottom: '4px'
                    }}>
                      {user.name || 'User'}
                    </p>
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#6B7280',
                      margin: 0
                    }}>
                      {user.email}
                    </p>
                    {user.roles && (
                      <div style={{ 
                        marginTop: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px'
                      }}>
                        {user.roles.map((role, idx) => (
                          <span key={idx} style={{
                            fontSize: '10px',
                            backgroundColor: '#E0F2F1',
                            color: '#1B5E5F',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            textTransform: 'capitalize'
                          }}>
                            {role.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Menu Items */}
                  <div style={{ padding: '8px' }}>
                    {user.roles?.includes('content_owner') || user.roles?.includes('admin') ? (
                      <button
                        onClick={() => {
                          setProfileDropdownOpen(false);
                          navigate('/my-content');
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 12px',
                          background: 'none',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: '#374151',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <User size={18} style={{ color: '#6B7280' }} />
                        {t('nav.myContent')}
                      </button>
                    ) : null}
                    
                    <button
                      onClick={() => {
                        setProfileDropdownOpen(false);
                        handleLogout();
                      }}
                      data-testid="logout-button"
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        background: 'none',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#DC2626',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#FEF2F2'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      <LogOut size={18} />
                      {t('nav.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => navigate('/login')}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#FFFFFF',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                }}
              >
                {t('nav.login')}
              </button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-menu-btn"
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              color: '#FFFFFF',
              padding: '8px',
              cursor: 'pointer'
            }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div style={{
          backgroundColor: '#164849',
          padding: '16px 24px',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }} className="mobile-menu">
          {navItems.filter(hasAccess).map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMobileMenuOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: isActive(item.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '15px',
                padding: '12px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                marginBottom: '4px'
              }}
            >
              {item.label}
            </button>
          ))}
          {user && (user.roles?.includes('content_owner') || user.roles?.includes('admin')) && (
            <button
              onClick={() => {
                navigate('/upload');
                setMobileMenuOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: isActive('/upload') ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '15px',
                padding: '12px 16px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              {t('nav.uploadContent')}
            </button>
          )}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {profileDropdownOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setProfileDropdownOpen(false)}
        />
      )}

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 900px) {
          .desktop-nav {
            display: none !important;
          }
          .desktop-only {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
        }
        @media (min-width: 901px) {
          .mobile-menu {
            display: none !important;
          }
        }
      `}</style>
    </header>
  );
}

export default LevelUpHeader;
